import { gql, useMutation } from "@apollo/client";
import { Avatar } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useCallback, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { emailExist } from "../../apollo/server";
import EmailImage from "../../assets/images/logo.png";
import FlashMessage from "../../components/FlashMessage";
import { LoginWrapper } from "../Wrapper";
import useStyles from "./styles";
import { useTranslation } from 'react-i18next';

function isValidEmailAddress(address) {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(address);
}

const EMAIL = gql`
  ${emailExist}
`;

function NewLogin() {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const [error, setError] = useState("");
  // null = not checked yet, "exists" = account found, "new" = no account
  const [checkResult, setCheckResult] = useState(null);
  const [checkedEmail, setCheckedEmail] = useState("");
  const formRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [EmailEixst, { loading }] = useMutation(EMAIL, {
    onCompleted,
    onError,
  });

  function onCompleted({ emailExist }) {
    setCheckResult(emailExist && emailExist._id ? "exists" : "new");
  }
  function onError() {
    setError("Something went wrong. Please try again.");
  }

  const handleAction = () => {
    const emailValue = formRef.current["email"].value;
    if (isValidEmailAddress(emailValue)) {
      setError("");
      setCheckedEmail(emailValue);
      EmailEixst({ variables: { email: emailValue } });
    } else {
      setError("Invalid Email");
    }
  };

  const goToLogin = () => {
    navigate("/login-email", {
      replace: true,
      state: { email: checkedEmail, from: location.state?.from },
    });
  };
  const goToRegister = () => {
    navigate("/registration", {
      replace: true,
      state: { email: checkedEmail, from: location.state?.from },
    });
  };

  // If the user edits the email after a check, reset the result
  const handleEmailChange = () => {
    if (checkResult) setCheckResult(null);
  };

  const toggleSnackbar = useCallback(() => {
    setError("");
  }, []);

  return (
    <LoginWrapper>
      <FlashMessage
        open={Boolean(error)}
        severity={"error"}
        alertMessage={error}
        handleClose={toggleSnackbar}
      />
      <Box display="flex">
        <Box m="auto" sx={{ width: 90, height: 64, overflow: "hidden" }}>
          <img
            alt="RushMyFood"
            src={EmailImage}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "top",
              transform: "scale(1.55)",
              transformOrigin: "top center",
            }}
          />
        </Box>
      </Box>
      <Box mt={theme.spacing(1)} />
      <Typography variant="h5" className={classes.font700}>
        {t('whatsYourEmail')}
      </Typography>
      <Box mt={theme.spacing(1)} />
      <Typography
        variant="caption"
        className={`${classes.caption} ${classes.fontGrey}`}
      >
        {checkResult === "exists"
          ? "Good news — you already have an account. Continue to sign in."
          : checkResult === "new"
          ? "We couldn't find an account with this email. Continue to create one."
          : t('checkAccount')}
      </Typography>
      <Box mt={theme.spacing(4)} />
      <form ref={formRef}>
        <TextField
          name={"email"}
          error={Boolean(error)}
          helperText={error}
          variant="outlined"
          defaultValue={""}
          label="Email"
          type={"email"}
          fullWidth
          onChange={handleEmailChange}
          InputLabelProps={{
            style: {
              color: theme.palette.grey[600],
            },
          }}
        />
        <Box mt={theme.spacing(8)} />

        {checkResult === "exists" ? (
          <Button
            variant="contained"
            fullWidth
            disableElevation
            className={`${classes.btnBase} ${classes.customBtn}`}
            onClick={(e) => { e.preventDefault(); goToLogin(); }}
          >
            <Typography variant="caption" className={`${classes.caption} ${classes.font700}`}>
              Continue to Login
            </Typography>
          </Button>
        ) : checkResult === "new" ? (
          <Button
            variant="contained"
            fullWidth
            disableElevation
            className={`${classes.btnBase} ${classes.customBtn}`}
            onClick={(e) => { e.preventDefault(); goToRegister(); }}
          >
            <Typography variant="caption" className={`${classes.caption} ${classes.font700}`}>
              Continue to Register
            </Typography>
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            type="email"
            disableElevation
            disabled={loading}
            className={`${classes.btnBase} ${classes.customBtn}`}
            onClick={(e) => {
              e.preventDefault();
              handleAction();
            }}
          >
            {loading ? (
              <CircularProgress color="primary" />
            ) : (
              <Typography
                variant="caption"
                className={`${classes.caption} ${classes.font700}`}
              >
                {t('continue')}
              </Typography>
            )}
          </Button>
        )}
      </form>
    </LoginWrapper>
  );
}

export default NewLogin;
